import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, PenLine, MessageCircle } from 'lucide-react';
import { fetchPerspectiveGroupResponses } from '../../../apis/perspectives.api';

const FEATURED_THRESHOLD = 50;

const LeafIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75" />
  </svg>
);

interface AllResponsesPanelProps {
  slug: string;
  communityResponseCount: number;
  groupId?: string;
  groupSlug?: string;
  groupTitle?: string;
}

const AllResponsesPanel: React.FC<AllResponsesPanelProps> = ({
  slug,
  communityResponseCount,
  groupId,
  groupSlug,
  groupTitle
}) => {
  const [expanded, setExpanded] = useState(false);
  const [sortBy, setSortBy] = useState('twigs');
  const navigate = useNavigate();

  const createParams = groupId
    ? `?perspectiveGroupId=${groupId}&perspectiveGroupSlug=${groupSlug || ''}&perspectiveGroupTitle=${encodeURIComponent(groupTitle || '')}`
    : '';

  const { data } = useQuery({
    queryKey: ['perspectiveResponses', slug],
    queryFn: () => fetchPerspectiveGroupResponses(slug, { limit: 20 }),
    enabled: expanded,
    staleTime: 60_000
  });

  const responses = data?.data || [];
  const totalCount = communityResponseCount || responses.length;

  return (
    <div className="mx-auto px-4 md:px-0 pb-5 max-w-[812px]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-3.5 border border-slate-200 bg-white cursor-pointer flex items-center justify-between transition-all"
        style={{ borderRadius: expanded ? '14px 14px 0 0' : '14px' }}
      >
        <div className="flex items-center gap-2.5">
          <span
            className="text-sm font-bold text-slate-900"
            style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
          >
            All Community Responses
          </span>
          <span
            className="text-[11px] px-2 py-0.5 rounded-full font-bold"
            style={{
              background: 'rgba(5,150,105,0.08)',
              color: '#059669',
              fontFamily: "'JetBrains Mono', monospace"
            }}
          >
            {totalCount}
          </span>
        </div>
        <ChevronDown
          size={16}
          className="text-slate-400 transition-transform duration-200"
          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }}
        />
      </button>

      {expanded && (
        <div className="border border-slate-200 border-t-0 rounded-b-[14px] bg-white overflow-hidden">
          {/* Sort bar */}
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <LeafIcon size={14} />
              <span>
                <strong className="text-green-600">{FEATURED_THRESHOLD} twigs</strong> to be
                featured in the perspective toggle
              </span>
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-2 py-1 rounded-md border border-slate-200 text-xs text-slate-500 bg-white cursor-pointer"
            >
              <option value="twigs">Most Twigged</option>
              <option value="newest">Newest</option>
              <option value="comments">Most Discussed</option>
            </select>
          </div>

          {/* Responses list */}
          <div className="max-h-[400px] overflow-y-auto">
            {responses.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-slate-400">
                No community responses yet. Be the first to share your perspective.
              </div>
            ) : (
              responses.map((item: any, i: number) => {
                const twigs = item.reactionNumber || 0;
                const progress = Math.min((twigs / FEATURED_THRESHOLD) * 100, 100);
                const nearThreshold = progress >= 60;
                return (
                  <div
                    key={item.id}
                    className="px-5 py-4 cursor-pointer transition-colors"
                    style={{
                      borderBottom: i < responses.length - 1 ? '1px solid #f1f5f9' : 'none'
                    }}
                    onClick={() => navigate(`/articles/${item.id}`)}
                  >
                    <div className="flex gap-3">
                      <div
                        className="w-9 h-9 rounded-full bg-slate-200 shrink-0 flex items-center justify-center text-[11px] font-bold text-slate-600"
                        style={{ fontFamily: "'JetBrains Mono', monospace" }}
                      >
                        {(item.title || '??').substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-sm font-bold text-slate-900 leading-tight mb-1"
                          style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
                        >
                          {item.title}
                        </div>
                        {item.subTitle && (
                          <p
                            className="text-[13px] text-slate-500 leading-snug mb-2.5 truncate"
                            style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
                          >
                            {item.subTitle}
                          </p>
                        )}
                        <div className="flex items-center gap-2.5">
                          <div className="flex-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-300"
                              style={{
                                width: `${progress}%`,
                                background: nearThreshold
                                  ? 'linear-gradient(to right, #059669, #10b981)'
                                  : '#d1d5db'
                              }}
                            />
                          </div>
                          <span
                            className="flex items-center gap-1 text-[11px] font-semibold whitespace-nowrap"
                            style={{
                              color: nearThreshold ? '#059669' : '#94a3b8',
                              fontFamily: "'JetBrains Mono', monospace"
                            }}
                          >
                            <LeafIcon size={12} />
                            {twigs} / {FEATURED_THRESHOLD}
                          </span>
                          <span className="flex items-center gap-1 text-[11px] text-slate-400">
                            <MessageCircle size={12} /> {item.commentNumber || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* CTA footer */}
          <div className="px-5 py-3.5 border-t border-slate-100 flex items-center justify-center">
            <button
              onClick={() => navigate(`/article/create${createParams}`)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-dashed border-green-600 text-[13px] font-semibold text-green-600 cursor-pointer hover:bg-green-50 transition"
              style={{ background: 'rgba(5,150,105,0.03)' }}
            >
              <PenLine size={14} /> Write Your Own Perspective
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllResponsesPanel;
